import React, { useCallback, useState, useRef } from 'react';
import { CloudArrowUp, Folder as FolderIcon, Image as ImageIcon } from '@phosphor-icons/react';

/**
 * Reusable drag & drop zone for files/photos/folders.
 *
 * Props:
 * - onFiles: (File[]) => void   REQUIRED, called when user drops/selects files
 * - multiple: boolean           allow multi-file selection (default true)
 * - accept: string              input accept attribute (e.g. "image/*", ".pdf")
 * - allowFolders: boolean       allow drop of full folders (reads recursively)
 * - showButton: boolean         show a "Choisir un fichier" button (default true)
 * - label: string               custom text
 * - icon: ReactNode             custom icon override
 * - testId: string              data-testid
 * - disabled: boolean
 */
const readEntry = (entry, path = '') => new Promise((resolve) => {
  if (entry.isFile) {
    entry.file((file) => {
      try {
        Object.defineProperty(file, 'relativePath', { value: path + file.name, writable: false });
      } catch (_) { /* frozen - ignore */ }
      resolve([file]);
    }, () => resolve([]));
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    const all = [];
    const readBatch = () => {
      reader.readEntries(async (entries) => {
        if (!entries.length) return resolve(all);
        const results = await Promise.all(entries.map(e => readEntry(e, path + entry.name + '/')));
        results.forEach(r => all.push(...r));
        readBatch();
      }, () => resolve(all));
    };
    readBatch();
  } else {
    resolve([]);
  }
});

const DropZone = ({
  onFiles,
  multiple = true,
  accept,
  allowFolders = true,
  showButton = true,
  label,
  hint,
  icon,
  testId = 'dropzone',
  disabled = false,
  compact = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [browsing, setBrowsing] = useState(false);
  const inputRef = useRef(null);
  const dirInputRef = useRef(null);

  const handleFiles = useCallback((files) => {
    if (!files || !files.length) return;
    const arr = Array.from(files);
    onFiles(multiple ? arr : [arr[0]]);
  }, [onFiles, multiple]);

  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const items = e.dataTransfer.items;
    // Use webkitGetAsEntry when available for directory support
    if (allowFolders && items && items.length && items[0].webkitGetAsEntry) {
      const all = [];
      const promises = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) promises.push(readEntry(entry));
      }
      const results = await Promise.all(promises);
      results.forEach(r => all.push(...r));
      if (all.length) handleFiles(all);
    } else {
      handleFiles(e.dataTransfer.files);
    }
  }, [allowFolders, disabled, handleFiles]);

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setIsDragging(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };

  const Icon = icon || (accept?.includes('image') ? ImageIcon : CloudArrowUp);

  return (
    <div
      data-testid={testId}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onPaste={(e) => {
        if (disabled) return;
        const files = Array.from(e.clipboardData?.files || []);
        if (files.length) { e.preventDefault(); handleFiles(files); }
      }}
      style={{
        border: `2px dashed ${isDragging ? '#002FA7' : '#BFC3C9'}`,
        borderRadius: '10px',
        padding: compact ? '18px 14px' : '28px 18px',
        textAlign: 'center',
        background: isDragging ? '#E3F2FD' : '#FAFAFB',
        color: '#5E5F66',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
      }}
      onClick={(e) => {
        if (disabled) return;
        // Avoid triggering when clicking a child button
        if (e.target.closest('button')) return;
        inputRef.current?.click();
      }}
    >
      <Icon size={compact ? 28 : 36} color={isDragging ? '#002FA7' : '#5E5F66'} weight={isDragging ? 'fill' : 'regular'} />
      <div style={{ fontWeight: '600', marginTop: '8px', color: '#1A1A1E', fontSize: compact ? '0.88rem' : '0.95rem' }}>
        {label || (isDragging ? 'Relâchez pour déposer' : 'Glissez-déposez ici ou cliquez')}
      </div>
      {hint !== false && (
        <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>
          {hint || (allowFolders ? 'Fichiers, photos et dossiers acceptés' : 'Fichiers acceptés')}
          {accept && <span style={{ marginLeft: '4px', opacity: 0.7 }}>({accept})</span>}
        </div>
      )}

      {showButton && !disabled && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: compact ? '10px' : '14px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            data-testid={`${testId}-browse-btn`}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          >
            <CloudArrowUp size={14} /> Choisir {multiple ? 'des fichiers' : 'un fichier'}
          </button>
          {allowFolders && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              data-testid={`${testId}-browse-folder-btn`}
              onClick={(e) => { e.stopPropagation(); setBrowsing(true); dirInputRef.current?.click(); setTimeout(() => setBrowsing(false), 200); }}
            >
              <FolderIcon size={14} /> Choisir un dossier
            </button>
          )}
        </div>
      )}
      {browsing && null}

      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
        style={{ display: 'none' }}
      />
      {allowFolders && (
        <input
          ref={dirInputRef}
          type="file"
          webkitdirectory=""
          directory=""
          multiple
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
};

export default DropZone;
